import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = () => {
  const faqs = [
    {
      question: "Are AI ads available right now?",
      answer: "AI advertising is in its early stages. Platforms like Perplexity already offer sponsored results, and we expect ChatGPT, Gemini, and others to open advertising soon. By getting started now, you'll be first in line when these platforms launch ads, giving you a massive competitive advantage.",
    },
    {
      question: "How much does it cost?",
      answer: "Our pricing is customized based on your industry, target area, and goals. We offer flexible packages starting from a setup fee plus monthly management. Book a free audit call to get a personalized quote — there's no obligation.",
    },
    {
      question: "What results can I expect?",
      answer: "Results vary by industry and location, but our goal is to generate qualified leads that turn into paying customers. We focus on lead generation for local service businesses, so you'll see phone calls, form submissions, and consultation bookings.",
    },
    {
      question: "How is this different from Google Ads?",
      answer: "Google Ads are keyword-based — you bid on search terms. AI advertising is conversation-based — your business appears when AI assistants recommend services to users. It's more natural, less competitive (for now), and reaches the growing audience that prefers asking AI over traditional search.",
    },
    {
      question: "What industries do you work with?",
      answer: "We specialize in local service businesses across the USA and Canada: lawyers, plumbers, HVAC, commercial cleaners, dentists, real estate agents, auto services, salons, restaurants, and home service providers. If your customers search for your service locally, we're likely a great fit.",
    },
    {
      question: "How automated is this really?",
      answer: "Highly automated. We use AI to generate ad creatives, optimize targeting, and create reports. You'll have a dedicated client portal where you can see everything happening. Our team handles the strategy and optimization while you focus on running your business.",
    },
  ];

  const leftFaqs = faqs.slice(0, 3);
  const rightFaqs = faqs.slice(3);

  return (
    <section id="faq" className="py-12 md:py-16 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-secondary/30 to-transparent" />
      
      <div className="container mx-auto px-4 max-w-[1280px] relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-4 mb-10"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            Frequently Asked <span className="text-gradient">Questions</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Got questions? We've got answers. If you don't see yours here, reach out — we're happy to help.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid md:grid-cols-2 gap-4"
        >
          {/* Left Column */}
          <Accordion type="single" collapsible className="space-y-3">
            {leftFaqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-left-${index}`}
                className="glass rounded-xl px-5 border-none hover:border-primary/30 transition-colors data-[state=open]:border-primary/50"
              >
                <AccordionTrigger className="text-left hover:no-underline hover:text-primary py-4 transition-colors">
                  <span className="font-semibold pr-4 text-sm">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4 text-sm leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Right Column */}
          <Accordion type="single" collapsible className="space-y-3">
            {rightFaqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-right-${index}`}
                className="glass rounded-xl px-5 border-none hover:border-primary/30 transition-colors data-[state=open]:border-primary/50"
              >
                <AccordionTrigger className="text-left hover:no-underline hover:text-primary py-4 transition-colors">
                  <span className="font-semibold pr-4 text-sm">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4 text-sm leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
